import React, { useMemo, useState } from 'react'
import { omit } from 'lodash-es'
import { Autocomplete, Box, IconButton, InputAdornment, Paper, PaperProps, TextField, Typography } from '@mui/material'
import { Add, Check, Close, Search } from '@mui/icons-material'
import { useAppTranslations } from '../../../utils/hooks/useAppTranslations'
import { TargetingCircle } from '../../../__generated__/graphql'
import { LocationTypesEnum } from '../../../pages/adgroup/settings/geoTargeting/GeoTargetingFields'
import { PlaceType } from '../autoCompleteGoogleMapField/intefaces'
import { useAutoComplete, UseAutoCompleteCallbacks } from '../autoCompleteGoogleMapField/useAutoComplete'
import { GeoLocation } from './types'
import { getLocationName, normalizeSearch } from './sortLocations'

const inputSx = { '& .MuiInputBase-root': { minHeight: 38 } } as const

type CommonProps = {
  query: string
  onQueryChange: (query: string) => void
  matchingCount: number
  total: number
}

const MatchCount: React.FC<{ query: string; matchingCount: number; total: number }> = ({
  query,
  matchingCount,
  total,
}) => {
  const t = useAppTranslations()
  if (!query.trim()) return null
  return (
    <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap', mr: 0.5 }}>
      {t('adgroup.geoTargeting.panel.matchCount', { shown: matchingCount, total })}
    </Typography>
  )
}

type Suggestion = PlaceType & { isTargeted: boolean }

/**
 * Every Google suggestion, those named like a targeted location marked: another place may share the name
 * (Saint-Denis), and only a real duplicate is refused once Google returns its details
 */
const useMarkedPlaces = (options: PlaceType[], locations: GeoLocation[]): Suggestion[] => {
  const targetedNames = useMemo(
    () => new Set(locations.map((location) => normalizeSearch(getLocationName(location)))),
    [locations]
  )
  return useMemo(
    () =>
      options.map((option) => ({
        ...option,
        isTargeted: targetedNames.has(normalizeSearch(option.structured_formatting?.main_text ?? option.description)),
      })),
    [options, targetedNames]
  )
}

const createPlaceOptionRenderer =
  (targetedHint: string) =>
  (props: React.HTMLAttributes<HTMLLIElement>, option: Suggestion): React.ReactNode => {
    const { key } = props as typeof props & { key?: React.Key }
    return (
      <Box component="li" key={key} {...omit(props, ['key'])} sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        {option.isTargeted ? <Check fontSize="small" color="disabled" /> : <Add fontSize="small" color="primary" />}
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {option.structured_formatting?.main_text}
        </Typography>
        <Typography variant="body2" color="textSecondary" noWrap sx={{ flex: 1, minWidth: 0 }}>
          {option.structured_formatting?.secondary_text}
        </Typography>
        {option.isTargeted && (
          <Typography variant="caption" color="textSecondary" sx={{ whiteSpace: 'nowrap' }}>
            {targetedHint}
          </Typography>
        )}
      </Box>
    )
  }

type SuggestionsPaperProps = PaperProps & { footerText?: string; enterHint?: string }

// Defined once, so the popup is not remounted when its footer text changes; the texts come through slotProps
const SuggestionsPaper: React.FC<SuggestionsPaperProps> = ({ children, footerText, enterHint, ...props }) => (
  <Paper {...props}>
    {children}
    <Box
      onMouseDown={(event) => event.preventDefault()}
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 1,
        px: 1.5,
        py: 0.75,
        borderTop: 1,
        borderColor: 'divider',
        bgcolor: 'action.hover',
      }}
    >
      <Typography variant="body2" color="textSecondary">
        {footerText}
      </Typography>
      <Typography variant="body2" color="textSecondary">
        {enterHint}
      </Typography>
    </Box>
  </Paper>
)

/** Read mode: filters the targeted locations, never calls Google */
export const ReadSearchInput: React.FC<CommonProps & { label: string }> = ({
  query,
  onQueryChange,
  matchingCount,
  total,
  label,
}) => {
  const t = useAppTranslations()

  return (
    <TextField
      type="search"
      size="small"
      fullWidth
      value={query}
      onChange={(event) => onQueryChange(event.target.value)}
      placeholder={label}
      sx={inputSx}
      slotProps={{
        htmlInput: { 'aria-label': label },
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: query ? (
            <InputAdornment position="end">
              <MatchCount query={query} matchingCount={matchingCount} total={total} />
              <IconButton
                size="small"
                onClick={() => onQueryChange('')}
                aria-label={t('adgroup.geoTargeting.panel.clearSearch')}
              >
                <Close fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : undefined,
        },
      }}
    />
  )
}

type EditSearchInputProps = CommonProps &
  UseAutoCompleteCallbacks & {
    name: string
    locationType: LocationTypesEnum
    countryCode: string
    /** Id of the input, also used to anchor the Google Places service */
    autocompleteId: string
    locations: GeoLocation[]
    error?: boolean
    /** The locations are excluded rather than targeted */
    exclusive?: boolean
  }

/**
 * Edit mode: one input that both filters the targeted locations and offers Google places to add.
 * Places named like a targeted location are marked, and Enter adds the first suggestion.
 */
export const EditSearchInput: React.FC<EditSearchInputProps> = ({
  query,
  onQueryChange,
  matchingCount,
  total,
  name,
  locationType,
  countryCode,
  autocompleteId,
  locations,
  onAdded,
  onDuplicate,
  error,
  exclusive = false,
}) => {
  const placeholderKey = exclusive
    ? 'adgroup.geoTargeting.panel.searchOrExclude'
    : 'adgroup.geoTargeting.panel.searchOrAdd'
  const t = useAppTranslations()
  const [open, setOpen] = useState(false)
  const { options, onChange, setInputValue } = useAutoComplete(locationType, name, countryCode, autocompleteId, {
    onAdded,
    onDuplicate,
  })
  const suggestions = useMarkedPlaces(options, locations)
  const renderPlaceOption = useMemo(
    () => createPlaceOptionRenderer(t('adgroup.geoTargeting.panel.suggestionTargeted')),
    [t]
  )

  const updateQuery = (value: string): void => {
    onQueryChange(value)
    setInputValue(value)
  }

  const footerText =
    matchingCount > 0
      ? t('adgroup.geoTargeting.panel.alreadyTargeted', { count: matchingCount })
      : t('adgroup.geoTargeting.panel.noneTargeted')

  return (
    <Autocomplete<Suggestion>
      id={autocompleteId}
      open={open && query.trim().length > 0}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={suggestions}
      filterOptions={(x) => x}
      autoHighlight
      value={null}
      inputValue={query}
      onInputChange={(_, value, reason) => {
        if (reason === 'input' || reason === 'clear') updateQuery(value)
      }}
      onChange={(_, option) => {
        if (!option) return
        onChange([...locations, option] as Array<PlaceType | TargetingCircle>)
        updateQuery('')
      }}
      getOptionLabel={(option) => option.description ?? ''}
      groupBy={() => t('adgroup.geoTargeting.panel.addToTargeting')}
      noOptionsText={t('adgroup.geoTargeting.panel.noNewPlace', { query: query.trim() })}
      slots={{ paper: SuggestionsPaper }}
      // The footer reminds what the list below shows and how to add from the keyboard
      slotProps={{
        paper: { footerText, enterHint: t('adgroup.geoTargeting.panel.enterToAdd') } as SuggestionsPaperProps,
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          error={error}
          placeholder={t(placeholderKey)}
          sx={inputSx}
          slotProps={{
            ...params.slotProps,
            htmlInput: { ...params.slotProps.htmlInput, 'aria-label': t(placeholderKey) },
            input: {
              ...params.slotProps.input,
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  <MatchCount query={query} matchingCount={matchingCount} total={total} />
                  {params.slotProps.input.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
      renderOption={renderPlaceOption}
    />
  )
}
